import { TelegramAltIcon } from "@/components/Icons"
import { Modal } from "@/components/Modal"
import { RadioButton } from "@/components/RadioButton"
import { SHARED_DATA_STATUS } from "@/domain/sharedData/constants"
import { getSharedEntityType } from "@/domain/sharedData/getSharedUtils"
import { useControlledRadioProps } from "@/hooks/useControlledRadioProps"
import { useGenericErrorToast } from "@/hooks/useGenericErrorToast"
import {
  type SharedDataJsonldReadCollectionSharedData,
  type SharedDataPatchSharedDataInputWriteItemSharedData,
  getGetUserSharedDataQueryKey,
  getGetUserSharedDatasQueryKey,
  useUpdateUserSharedData,
} from "@/networking/api"
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  ModalBody,
  ModalFooter,
  Textarea,
} from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { FormattedMessage } from "react-intl"
import SharedEntityName from "./SharedEntityName"
import { SHARING_RESPONSE_MOTIVATION } from "./types"

type FormType = Partial<SharedDataPatchSharedDataInputWriteItemSharedData>

type Props = {
  isOpen: boolean
  onClose: () => void
  sharedEntity: SharedDataJsonldReadCollectionSharedData
  isRevoke: boolean
}

export default function ShareDeclineModal({ isOpen, onClose, sharedEntity, isRevoke }: Props) {
  const entityType = getSharedEntityType(sharedEntity)

  const defaultValues = {
    messageAnswer: "",
    subjectAnswer: SHARING_RESPONSE_MOTIVATION.ALREADY_KNOWS_CUSTOMER as string,
    // jhg
  }

  const { control, handleSubmit, register } = useForm<FormType>({
    defaultValues,
  })

  const accessRequestMotivationOptions = [
    {
      value: SHARING_RESPONSE_MOTIVATION.ALREADY_KNOWS_CUSTOMER,
      translationId: `userSharedData.${entityType}.motivation.${SHARING_RESPONSE_MOTIVATION.ALREADY_KNOWS_CUSTOMER}`,
    },
    {
      value: SHARING_RESPONSE_MOTIVATION.PROJECT_NOT_POSSIBLE,
      translationId: `userSharedData.${entityType}.motivation.${SHARING_RESPONSE_MOTIVATION.PROJECT_NOT_POSSIBLE}`,
    },
    {
      value: SHARING_RESPONSE_MOTIVATION.NOT_COMPETENT,
      translationId: `userSharedData.${entityType}.motivation.${SHARING_RESPONSE_MOTIVATION.NOT_COMPETENT}`,
    },
    {
      value: SHARING_RESPONSE_MOTIVATION.OTHER,
      translationId: `userSharedData.${entityType}.motivation.${SHARING_RESPONSE_MOTIVATION.OTHER}`,
    },
  ]

  const { getRadioProps: getMotivationRadioProps } = useControlledRadioProps({
    control,
    name: "subjectAnswer",
    defaultValue: defaultValues.subjectAnswer,
    rules: { required: true },
  })

  const { mutate, isPending } = useUpdateUserSharedData()
  const queryClient = useQueryClient()
  const genericErrorToast = useGenericErrorToast()

  const postShareRequest = (formData: FormType) => {
    mutate(
      {
        data: {
          ...formData,
          id: sharedEntity.id,
          status: isRevoke ? SHARED_DATA_STATUS.REVOKED : SHARED_DATA_STATUS.DENIED,
        },
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: getGetUserSharedDataQueryKey(sharedEntity.id),
          })
          void queryClient.invalidateQueries({
            queryKey: getGetUserSharedDatasQueryKey(),
          })
          onClose()
        },
        onError: genericErrorToast,
      }
    )
  }

  return (
    <Modal
      {...{ isOpen, onClose }}
      title={<FormattedMessage id={`userSharedData.${entityType}.modal.title_decline`} />}
      scrollBehavior="outside"
    >
      <form onSubmit={handleSubmit(postShareRequest)}>
        <ModalBody>
          <FormControl as={Flex} flexDir="column" gap="6">
            <Box>
              <FormLabel>
                <FormattedMessage id="userSharedData.object" />
              </FormLabel>
              <SharedEntityName item={sharedEntity} displayInputLike={true} />
            </Box>

            <Box>
              <FormLabel>
                <FormattedMessage id="userSharedData.motivation" />
              </FormLabel>
              <Flex justify="space-between">
                {accessRequestMotivationOptions.map(subject => {
                  return (
                    <RadioButton
                      key={subject.value}
                      labelTranslationId={subject.translationId}
                      {...getMotivationRadioProps({ value: subject.value })}
                    />
                  )
                })}
              </Flex>
            </Box>

            <Box>
              <FormLabel>
                <FormattedMessage id="userSharedData.comment" />
              </FormLabel>
              <Textarea {...register("messageAnswer")} variant="outlineWithBorder" />
            </Box>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button display="flex" gap="2" type="submit" isLoading={isPending}>
            <FormattedMessage id="send" />
            <TelegramAltIcon boxSize={4} />
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
